import anylogger from '@app/anylogger'
import { useCurrentRef } from '@app/hooks'
import { Flex, FlexC, useCurrentBreakpoint } from '@app/muiplus'
import { datesAreEqual, parseDateTime } from '@app/utils'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, lighten, useTheme } from '@mui/material'
import { useCallback, useContext, useRef, useState } from 'react'
import { usePermission } from 'src/services/usePermission'
import { CalendarContext } from './CalendarContext'
import { CalendarDetails } from './CalendarDetails'
import { CalendarDisplayEvent } from './CalendarDisplayEvent'
import { CalendarEvent } from './CalendarEvent'
import { parseCalendarEvent } from './calendarUtils'

const log = anylogger('CalendarDay')

export function CalendarDay({ date, events }: { date: string; events: CalendarDisplayEvent[] | undefined }) {
	const { selectedDate: selectedDateStr, setSelectedDate, addEvent } = useContext(CalendarContext)
	const [detailEvent, setDetailEvent] = useState<CalendarDisplayEvent | undefined>(undefined)
	const [ref, setRef] = useCurrentRef<HTMLElement>()
	const [isHovering, setIsHovering] = useState(false)

	const thisDate = parseDateTime(date)
	const today = parseDateTime(new Date())
	const selectedDate = parseDateTime(selectedDateStr)
	const theme = useTheme()
	const perm = usePermission()

	const onCloseDetails = useCallback(() => {
		setDetailEvent(undefined)
	}, [])
	const eventClick = useCallback((e: CalendarDisplayEvent) => {
		setDetailEvent(e)
	}, [])
	const tooltip = useRef<string>('')

	const getEvents = useCallback(() => {
		if (!events) return null
		const multiDay = events.filter((e) => e.multiDay)
		const hasMultiDaySiblings = multiDay.length > 0
		const singleDay = events.filter((e) => !e.multiDay)
		let res: any = []
		let idx = 0
		tooltip.current = ''
		while (multiDay.length || singleDay.length) {
			try {
				let md: CalendarDisplayEvent = multiDay[0]
				const sd = singleDay[0]
				if (md && md.slot == idx) {
					// if this multiDay event applies, then remove it from the list
					multiDay.shift()
					// log('md', md.event.summary, md)
					const { tooltip: tt } = parseCalendarEvent(md.event)
					if (tooltip.current.length > 0) tooltip.current += '\n\n'
					tooltip.current += tt
					res.push(
						<CalendarEvent
							key={idx}
							displayEvent={md}
							onClick={eventClick}
							isDetailed={md == detailEvent}
							hasMultiDaySiblings={hasMultiDaySiblings}
						/>
					)
				} else if (sd) {
					// log('filler', sd.event.summary, sd)
					singleDay.shift()
					const { tooltip: tt } = parseCalendarEvent(sd.event)
					if (tooltip.current.length > 0) tooltip.current += '\n\n'
					tooltip.current += tt

					res.push(
						<CalendarEvent
							key={idx}
							displayEvent={sd}
							onClick={eventClick}
							isDetailed={sd == detailEvent}
							hasMultiDaySiblings={hasMultiDaySiblings}
						/>
					)
				} else res.push(<CalendarEvent key={idx} displayEvent={undefined} hasMultiDaySiblings={hasMultiDaySiblings} />)
			} finally {
				idx++
			}
		}
		return res
	}, [detailEvent, eventClick, events])
	const setDate = useCallback(() => {
		setSelectedDate(date)
	}, [date, setSelectedDate])
	let bgColor = 'white'

	if (thisDate.mm != selectedDate.mm) bgColor = theme.palette.action.disabledBackground
	else if (datesAreEqual(thisDate, selectedDate)) bgColor = lighten(theme.palette.primary.light, 0.7)
	const bkp = useCurrentBreakpoint()
	let borderProps = {}
	// if (bkp == 'mobile') {
	borderProps = { borderRadius: '0px', gap: '0px' }
	// if (thisDate.mm != selectedDate.mm) bgColor = alpha('#FFFFFF', 0.12)
	// } else {
	// 	borderProps = { border: 'solid 1px gray', borderRadius: '0.5em' }
	// }
	const internalAddEvent = useCallback(() => {
		addEvent(date)
	}, [addEvent, date])
	const getAddButton = useCallback(() => {
		if (!perm.isCalendarEditor) return null
		return (
			<Flex position="absolute" bottom="0.25em" right="0.25em">
				<Button
					tooltip="Add Event"
					size="small"
					onClick={internalAddEvent}
					sx={{
						fontSize: '14px',
						px: '0px',
						py: '0px',
						mb: '0px',
						mr: '0px',
						minWidth: '0px',
						opacity: isHovering ? 0.6 : 0,
						transition: 'opacity 0.7s'
					}}
				>
					<AddIcon />
				</Button>
			</Flex>
		)
	}, [internalAddEvent, isHovering, perm.isCalendarEditor])

	const dateBgColor = datesAreEqual(thisDate, today) ? theme.palette.primary.light : undefined
	return (
		<FlexC
			ref={setRef}
			p="auto"
			{...borderProps}
			bgcolor={bgColor}
			onClick={setDate}
			minWidth="0"
			minHeight="0"
			position="relative"
			tooltip={tooltip.current}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			<Box
				mx="auto"
				bgcolor={dateBgColor}
				// px="0.25em"
				borderRadius="1000%"
				fontSize="1.3em"
				fontWeight="bold"
				sx={{
					aspectRatio: '1/1',
					textAlign: 'center',
					cursor: 'pointer',
					':hover': { backgroundColor: lighten(theme.palette.primary.light, 0.4) },
					transition: 'background-color 0.3s ease'
				}}
			>
				{thisDate.dd}
			</Box>
			<FlexC fill gap="2px" justifyContent="start">
				{getEvents()}
			</FlexC>
			<CalendarDetails displayEvent={detailEvent} onClose={onCloseDetails} anchorEl={ref} />
			{getAddButton()}
		</FlexC>
	)
}
